import { useCustom, useCustomMutation, useGo } from '@refinedev/core';
import { Loader2, Plus, Trash2, Eye, Webhook } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { FormHeader } from '@/components/forms';
import { useScrollRestoration } from '@/hooks/use-scroll-restoration';
import { useState } from 'react';
import type { LeadWebhook } from '../types';

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function LeadWebhooksList() {
  useScrollRestoration();
  const go = useGo();
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const webhooksQuery = useCustom<LeadWebhook[]>({
    url: '/leads/webhooks',
    method: 'get',
  });

  const isLoading = webhooksQuery.query?.isLoading ?? false;
  const refetch = webhooksQuery.query?.refetch;
  const webhooks = webhooksQuery.query?.data?.data ?? [];

  const deleteMutation = useCustomMutation();

  const handleDelete = () => {
    if (deleteId === null) { return; }
    deleteMutation.mutate(
      { url: `/leads/webhooks/${deleteId}`, method: 'delete', values: {} },
      {
        onSuccess: () => {
          setDeleteId(null);
          refetch?.();
        },
      },
    );
  };

  return (
    <div className="space-y-4">
      <FormHeader
        title="Webhook лидов"
        description="Настройка входящих webhook для создания лидов"
        onBack={() => go({ to: '/leads' })}
        icon={<Webhook className="h-5 w-5" />}
        sticky
        actions={
          <Button size="sm" onClick={() => go({ to: '/leads/webhooks/create' })}>
            <Plus className="h-4 w-4 mr-1" />
            Создать webhook
          </Button>
        }
      />

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      ) : (
        webhooks.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <Webhook className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">Нет webhook-конфигураций</p>
              <Button onClick={() => go({ to: '/leads/webhooks/create' })}>
                <Plus className="h-4 w-4 mr-1" />
                Создать первый webhook
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">#</TableHead>
                <TableHead>Название</TableHead>
                <TableHead>Статус</TableHead>
                <TableHead>Дата создания</TableHead>
                <TableHead className="w-24">Действия</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {webhooks.map((wh) => (
                <TableRow key={wh.id}>
                  <TableCell className="font-mono text-xs text-muted-foreground">#{wh.id}</TableCell>
                  <TableCell className="font-medium">{wh.name}</TableCell>
                  <TableCell>
                    <Badge variant={wh.isActive ? 'default' : 'secondary'}>
                      {wh.isActive ? 'Активен' : 'Отключён'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{formatDate(wh.createdAt)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => go({ to: `/leads/webhooks/show/${wh.id}` })}
                        title="Просмотр"
                      >
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive hover:text-destructive"
                        onClick={() => setDeleteId(wh.id)}
                        title="Удалить"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </Card>
        )
      )}

      <AlertDialog open={deleteId !== null} onOpenChange={(open) => { if (!open) { setDeleteId(null); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить webhook?</AlertDialogTitle>
            <AlertDialogDescription>
              Webhook будет удалён. Все лиды, созданные через него, останутся. Это действие нельзя отменить.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleteMutation.mutation.isPending}>
              {deleteMutation.mutation.isPending ? 'Удаление...' : 'Удалить'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
